import React from "react";
import { EllipsisVertical } from "lucide-react";

import { Switch } from "@/shared/components/ui/switch";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { GhostButton, StatusBadge } from "@/shared/components/settings-atoms";
import { Sparkline } from "@/shared/components/ui-kit";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { ago } from "@/shared/lib/time";
import { InterfaceIcon } from "./InterfaceIcon";

import type { Interface, InterfaceType } from "@/shared/types";

/** The action that repairs a broken interface, in each channel's terms. */
const REPAIR: Partial<Record<InterfaceType, string>> = {
  telegram: "Reconnect",
  slack: "Reinstall",
  whatsapp: "Link again",
};

/** Where group chats reach the agent, so the owner may turn them off. The WhatsApp bridge
    passes one-to-one chats only. */
const IN_GROUPS: InterfaceType[] = ["telegram", "slack"];

function Badge({ iface }: { iface: Interface }) {
  if (iface.connection === "needs_setup") return <StatusBadge tone="warn">Needs setup</StatusBadge>;
  if (iface.connection === "broken") return <StatusBadge tone="danger">Error</StatusBadge>;
  return iface.status === "live" ? <StatusBadge tone="success">Live</StatusBadge> : <StatusBadge>Paused</StatusBadge>;
}

/** Numbers when it works, the problem in plain words when it does not. */
function Summary({ iface }: { iface: Interface }) {
  const sep = <span className="text-[var(--text-quaternary)]"> · </span>;
  const label = iface.label && <>{iface.label}{sep}</>;
  if (iface.connection === "needs_setup") return <>{label}Setup isn't finished — nobody can reach it yet.</>;
  if (iface.connection === "broken") return <>{label}<span className="text-[var(--danger)]">{iface.error}</span></>;
  const stats = iface.stats;
  return (
    <>
      {label}
      <span className="font-mono tabular-nums">{stats?.messages_30d ?? 0}</span> messages
      {stats?.last_message_at && <>{sep}active {ago(stats.last_message_at)}</>}
    </>
  );
}

/* One interface. A working one shows its 14-day traffic; one that needs
   setup, or broke, shows the one action that fixes it instead. */
export function InterfaceRow({ iface, onToggle, onToggleGroups, onRepair, onTest, onSimulate, onViewChats, onOpenTraces, onDisconnect }: {
  iface: Interface;
  onToggle: () => void;
  /** Answer in group chats, or not. */
  onToggleGroups: () => void;
  onRepair: () => void;
  onTest: () => void;
  onSimulate: () => void;
  /** The conversations that came in through it. */
  onViewChats: () => void;
  onOpenTraces: () => void;
  onDisconnect: () => void;
}) {
  const meta = catalogFor(iface.type);
  if (!meta) return null;
  const live = iface.status === "live";
  const needsSetup = iface.connection === "needs_setup";
  const repair = needsSetup ? "Finish setup" : REPAIR[iface.type] ?? "Reconnect";
  const item = "px-2 py-[5px] rounded-[7px] text-[13px]";

  return (
    <div className="group flex flex-wrap items-center gap-x-3 gap-y-2 py-3.5">
      <InterfaceIcon meta={meta} />
      <div className="flex-1 basis-0 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="min-w-0 truncate text-[13.5px] font-medium text-[var(--text-primary)]">{meta.name}</span>
          <Badge iface={iface} />
        </div>
        {/* An error wraps rather than truncates: on a phone it is the one thing the row must say. */}
        <div className={`text-[12.5px] text-[var(--text-tertiary)] mt-[1px] ${iface.connection === "broken" ? "" : "truncate"}`}>
          <Summary iface={iface} />
        </div>
      </div>
      {iface.connection === "ready" ? (
        <button
          onClick={onOpenTraces}
          title="14-day traffic — open traces"
          className="press hidden sm:flex items-center rounded-[6px] px-1 py-0.5 hover:bg-[var(--accent-hover)]"
        >
          <Sparkline data={iface.stats?.traffic ?? []} className="text-[var(--text-tertiary)]" />
        </button>
      ) : (
        /* On a phone the fix takes a line of its own under the words it answers (pl = icon + gap),
           so they keep the row's width; from sm it sits beside them */
        <div className="order-last basis-full pl-[48px] sm:order-none sm:basis-auto sm:pl-0">
          <GhostButton onClick={onRepair}>{repair}</GhostButton>
        </div>
      )}
      {!needsSetup && (
        <Switch
          checked={live}
          onCheckedChange={onToggle}
          aria-label={`${iface.label ?? meta.name} live`}
          title={live ? "Pause interface" : "Go live"}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            title="Interface options"
            className="press w-7 h-7 shrink-0 rounded-full hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-tertiary)]"
          >
            <EllipsisVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
          {needsSetup ? (
            <DropdownMenuItem className={item} onClick={onRepair}>Finish setup</DropdownMenuItem>
          ) : (
            <>
              {iface.connection === "broken" && <DropdownMenuItem className={item} onClick={onRepair}>{repair}</DropdownMenuItem>}
              <DropdownMenuItem className={item} onClick={onTest}>Test connection</DropdownMenuItem>
              {IN_GROUPS.includes(iface.type) && (
                <DropdownMenuCheckboxItem
                  className={`${item} pl-7`}
                  checked={iface.allow_groups}
                  onCheckedChange={onToggleGroups}
                >
                  Answer in groups
                </DropdownMenuCheckboxItem>
              )}
              <DropdownMenuItem className={item} onClick={onSimulate}>Simulate incoming message</DropdownMenuItem>
              <DropdownMenuItem className={item} onClick={onViewChats}>View chats</DropdownMenuItem>
              <DropdownMenuItem className={item} onClick={onOpenTraces}>View traces</DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator className="my-0.5" />
          <DropdownMenuItem className={`${item} text-[var(--danger)] focus:text-[var(--danger)]`} onClick={onDisconnect}>
            Disconnect
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
